import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldValues,
  UseFieldArrayAppend,
  useFormContext,
} from 'react-hook-form';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import { AppendValue } from '@lib/components/ReactHookForm/types';
import FileUploader from '@lib/components/FilesUploader/FileUploader';
import {
  FileSizeLimits,
  formOptionsFileSizeLimit,
  UppyDataImportAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import { readFileData } from 'routes/FormChecklists/utils/readFileData';
import ChecklistFieldNames from '@lib/enums/fieldNames/checklistFieldNames';
import { captureException } from 'utils/captureException';
import downloadHandler from '@lib/utils/downloadHandler';
import { materialSymbolsOutlined } from '@lib/enums/common';
import toast from '@lib/components/Toast/Toast';
import styles from './ChecklistOptionsAddItemComponent.module.scss';

const { VITE_CDN_URL } = import.meta.env;

interface Props {
  append: UseFieldArrayAppend<FieldValues, string>;
  appendCallback: () => void;
  appendValue: AppendValue;
  isAddMoreVisible: boolean;
}

function ChecklistOptionsAddItemComponent({
  append,
  appendCallback,
  appendValue,
  isAddMoreVisible,
}: Props) {
  const { t, i18n } = useTranslation();
  const { setValue } = useFormContext();

  const uploadButtonProps = {
    buttonText: t('upload-file'),
    buttonType: ButtonTypes.primaryOutlined,
    buttonSize: ButtonSizes.small,
    leftIcon: 'upload',
    fullWidth: true,
  };

  const onFileUpload = (v) => {
    readFileData(v)
      .then((res) => {
        const newOptions = res
          .filter((option) => {
            let optionValue = option[0];
            if (typeof optionValue === 'string') {
              optionValue = optionValue.trim();
              return !!optionValue && optionValue?.[0] !== '⋄';
            }
            return false;
          })
          .map((option, position) => ({
            values: [option[0] ? `${option[0]}` : ''],
            position,
          }));
        if (newOptions.length) {
          setValue(ChecklistFieldNames.Options, newOptions);
        } else {
          toast({ content: t('no-options-import-error') });
        }
      })
      .catch((error) => {
        captureException(error);
      });
  };

  const onDownloadTemplate = () => {
    downloadHandler(
      `${VITE_CDN_URL}/templates/${i18n.language}/import/Checklists-import-template.xlsx`,
    );
  };

  const onAddOption = () => {
    append(appendValue);
    if (appendCallback) appendCallback();
  };

  return (
    <div className={styles.addItemBtnWrapper}>
      <Button
        buttonSize={ButtonSizes.small}
        buttonText={t('add-option')}
        buttonType={ButtonTypes.secondaryOutlined}
        disabled={!isAddMoreVisible}
        leftIcon="add"
        onClick={onAddOption}
      />
      <div>
        <Button
          buttonSize={ButtonSizes.small}
          buttonText={t('download-template')}
          buttonType={ButtonTypes.primaryOutlined}
          leftIcon="download"
          leftIconClassName={materialSymbolsOutlined}
          onClick={onDownloadTemplate}
        />
        <FileUploader
          allowedFileTypes={UppyDataImportAllowedFileTypes}
          maxFileSize={formOptionsFileSizeLimit}
          note={t('data-import-upload-validation-msg', {
            size: FileSizeLimits.FormOptionsFile,
          })}
          onChange={onFileUpload}
          showOfflineMessage
          uploadButtonProps={uploadButtonProps}
          useUploadStrategy={false}
        />
      </div>
    </div>
  );
}

export default ChecklistOptionsAddItemComponent;
